import React, { useState } from 'react';
import { Box, Flex, IconV2Props, TextV2 } from '@withjoy/joykit';
import { ChevronUp } from '@withjoy/joykit/icons';
import { animationTransition } from '@shared/utils/animationTransition';
import { Colors, pxToRem } from '@withjoy/joykit/theme';
import { boxShadowValuesV2 } from '@shared/utils/style/dropShadows';
import { AccordionOnOffBadge } from '../AccordionOnOffBadge';
import { SkeletonThumbnail } from '@shared/components/Skeleton';

export type AccordionItemProps = {
  icon: React.ReactElement<IconV2Props>;
  label: string;
  accentColor: keyof Colors;
  renderContent: () => React.ReactNode;
  renderStatusBadge?: () => React.ReactNode;
  renderPromoCard?: () => React.ReactNode;
  showBadge?: boolean;
  /** Only used when showBadge is true */
  onBadgeClick?: () => void;
  /** Only used when showBadge is true */
  badgeLabel?: string;
  /** Only used when showBadge is true */
  isEnabled?: boolean;
  onToggle?: (isOpen: boolean) => void;
};

const AccordionItem = ({
  icon,
  label,
  accentColor,
  renderContent,
  renderStatusBadge,
  renderPromoCard,
  showBadge,
  onBadgeClick,
  badgeLabel,
  isEnabled,
  onToggle
}: AccordionItemProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const localIcon = React.isValidElement(icon)
    ? React.cloneElement(icon as React.ReactElement<IconV2Props>, { color: isOpen ? 'white' : accentColor, transition: animationTransition('color') })
    : null;

  return (
    <Box width={'100%'}>
      <Flex
        onClick={e => {
          e.preventDefault();
          onToggle?.(!isOpen);
          setIsOpen(prev => !prev);
        }}
      >
        <Flex
          __css={{
            cursor: 'pointer',
            width: '100%',
            height: '74px',
            alignItems: 'center',
            backgroundColor: isOpen ? 'white' : 'mono1',
            borderRadius: '37px',
            paddingLeft: 3,
            paddingRight: 6,
            boxShadow: isOpen ? boxShadowValuesV2.xxxl : 'none',
            transition: animationTransition('box-shadow', 'background-color'),
            _hover: {
              boxShadow: boxShadowValuesV2.xxxl,
              backgroundColor: 'white'
            },
            _active: {
              boxShadow: 'none',
              backgroundColor: 'mono2'
            }
          }}
        >
          <Flex
            __css={{
              width: '58px',
              height: '58px',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isOpen ? accentColor : 'mono2',
              borderRadius: '50%',
              flexShrink: 0,
              transition: animationTransition('background-color')
            }}
          >
            {localIcon}
          </Flex>
          <TextV2 typographyVariant={'hed2'} marginLeft={4} flexGrow={1}>
            {label}
          </TextV2>
          {showBadge && typeof isEnabled === 'boolean' && typeof onBadgeClick === 'function' && (
            <AccordionOnOffBadge label={badgeLabel} isEnabled={isEnabled} onClick={onBadgeClick} />
          )}
          <ChevronUp transform={isOpen ? 'rotate(0)' : 'rotate(180deg)'} transition={animationTransition('transform')} size={'sm'} color={'mono8'} marginLeft={3} />
        </Flex>
      </Flex>
      <Box display={'grid'} gridTemplateRows={isOpen ? '1fr' : '0fr'} overflow="hidden" opacity={isOpen ? 1 : 0} transition={animationTransition('grid-template-rows', 'opacity')}>
        <Box overflow={'hidden'}>
          <Flex flexDirection={'column'} padding={5} rowGap={5}>
            {renderPromoCard && renderPromoCard()}
            {renderStatusBadge && renderStatusBadge()}
            <Box
              __css={{
                [':has(.dashboard-stat):has(.dashboard-accordion-menu)']: {
                  borderRadius: pxToRem(12),
                  backgroundColor: 'mono2'
                }
              }}
            >
              {renderContent()}
            </Box>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

const Skeleton = () => {
  return (
    <Flex
      __css={{
        width: '100%',
        height: '74px',
        alignItems: 'center',
        borderRadius: '37px',
        paddingLeft: 3,
        paddingRight: 6,
        backgroundColor: 'white',
        boxShadow: boxShadowValuesV2.xxxl,
        transition: animationTransition('box-shadow', 'background-color'),
        _hover: {
          boxShadow: boxShadowValuesV2.xxxl,
          backgroundColor: 'white'
        }
      }}
    >
      <Flex
        __css={{
          width: '58px',
          height: '58px',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          flexShrink: 0,
          overflow: 'hidden'
        }}
      >
        <SkeletonThumbnail height={pxToRem(58)} width={pxToRem(58)} />
      </Flex>
      <Box marginLeft={4} flexGrow={1}>
        <SkeletonThumbnail width={'50%'} height={pxToRem(24)} />
      </Box>
      <ChevronUp transform={'rotate(180deg)'} size={'sm'} color={'mono8'} marginLeft={3} />
    </Flex>
  );
};

AccordionItem.Skeleton = Skeleton;

export { AccordionItem };
